import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import type { Note } from '../../queries/usePaginateNotes'

interface UpdateNoteData {
  id: string
  title?: string
  content?: string
  contactAccountId?: string | null // null for personal notes, accountId for shared notes
  scope?: string
  isShared?: boolean
  isPinned?: boolean
}

const updateNote = async (data: UpdateNoteData): Promise<void> => {
  const { id, ...updateData } = data
  await api.put(`/note/${id}`, {
    title: updateData.title,
    content: updateData.content,
    contactAccountId: updateData.contactAccountId,
    scope: updateData.scope,
    isShared: updateData.isShared,
    isPinned: updateData.isPinned,
  })
}

// `silent`: the editor saves by itself while you type, and says « Enregistré »
// or « Non enregistré » in its header; a toast on every save, or on every try
// while the connection is down, would be noise.
export const useUpdateNote = ({ silent = false }: { silent?: boolean } = {}) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateNoteData>({
    mutationFn: updateNote,
    onSuccess: async (_, variables) => {
      if (!silent) showSuccessToast(t('toast.success'), t('toast.noteUpdated'))
      // The editor saves what is left as it closes: by then nobody watches the
      // note, so invalidating it does not fetch it again, and reopening it
      // showed the text from before that last save (the next edit then erased
      // the saved words). What was saved goes straight into the cached note.
      const { id, ...saved } = variables
      const changes = Object.fromEntries(
        Object.entries(saved).filter(([, value]) => value !== undefined),
      )
      queryClient.setQueryData<Note>(['note', id], (cached) => cached && { ...cached, ...changes })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['notes'] }),
        queryClient.invalidateQueries({ queryKey: ['note', variables.id] }),
        // « Historique avec X » on the contact's profile shows the note's title.
        queryClient.invalidateQueries({ queryKey: ['contact-history'] }),
      ])
    },
    onError: (error) => {
      if (!silent) showApiErrorToast(t, error)
    },
  })
}
